import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, updateDoc, addDoc, where, increment } from 'firebase/firestore';
import { db, getTimeData } from './firebase';
import QuestionCard from './components/QuestionCard';
import SubmitQuestion from './components/SubmitQuestion';
import Analytics from './components/Analytics';
import Terms from './components/Terms';

function App() {
  const [allQuestions, setAllQuestions] = useState([]);
  const [unvotedQuestions, setUnvotedQuestions] = useState([]);
  const [votedQuestions, setVotedQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [justVoted, setJustVoted] = useState(false);
  const [hasVotedInSession, setHasVotedInSession] = useState(false);
  const [userId] = useState(() => {
    let id = localStorage.getItem('atc-user-id');
    if (!id) {
      id = 'user-' + Math.random().toString(36).substr(2, 9) + Date.now();
      localStorage.setItem('atc-user-id', id);
    }
    return id;
  });

  useEffect(() => {
    loadQuestionsAndVotes();
  }, []);

  const loadQuestionsAndVotes = async () => {
    try {
      const questionsQuery = query(
        collection(db, 'questions'),
        where('status', '==', 'active')
      );
      
      const questionsSnapshot = await getDocs(questionsQuery);
      const questions = [];
      const now = new Date();
      const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      
      questionsSnapshot.forEach((doc) => {
        const data = doc.data();
        const createdAt = data.createdAt?.toDate();
        
        // Only include questions created within the last 24 hours
        if (createdAt && createdAt > twentyFourHoursAgo) {
          questions.push({ id: doc.id, ...data });
        }
      });

      const votesQuery = query(
        collection(db, 'votes'),
        where('voterId', '==', userId)
      );
      
      const votesSnapshot = await getDocs(votesQuery);
      const votedQuestionIds = new Set();
      votesSnapshot.forEach((doc) => {
        votedQuestionIds.add(doc.data().questionId);
      });

      const voted = questions.filter(q => votedQuestionIds.has(q.id));
      const unvoted = questions.filter(q => !votedQuestionIds.has(q.id));

      const shuffledUnvoted = unvoted.sort(() => Math.random() - 0.5);

      setAllQuestions(questions);
      setVotedQuestions(voted);
      setUnvotedQuestions(shuffledUnvoted);
      setCurrentQuestion(shuffledUnvoted[0] || null);
      setLoading(false);
    } catch (error) {
      console.error('Error loading questions:', error);
      setLoading(false);
    }
  };

  const handleVote = async (questionId, choice) => {
    try {
      setJustVoted(true);
      setHasVotedInSession(true);
      const timeData = getTimeData();
      
      await addDoc(collection(db, 'votes'), {
        questionId,
        voterId: userId,
        choice,
        timestamp: new Date(),
        votedAt: {
          hour: timeData.hour,
          dayOfWeek: timeData.dayOfWeek,
          date: timeData.date
        }
      });

      const questionRef = doc(db, 'questions', questionId);
      const field = choice === 'A' ? 'votesA' : 'votesB';
      
      await updateDoc(questionRef, {
        [field]: increment(1),
        totalVotes: increment(1)
      });

      const votedQuestion = unvotedQuestions.find(q => q.id === questionId);
      if (votedQuestion) {
        votedQuestion[field] = (votedQuestion[field] || 0) + 1;
        votedQuestion.totalVotes = (votedQuestion.totalVotes || 0) + 1;
      }

      const remainingUnvoted = unvotedQuestions.filter(q => q.id !== questionId);
      setUnvotedQuestions(remainingUnvoted);
      setVotedQuestions([...votedQuestions, votedQuestion]);

      setTimeout(() => {
        setJustVoted(false);
        if (remainingUnvoted.length > 0) {
          setCurrentQuestion(remainingUnvoted[0]);
        } else {
          setCurrentQuestion(null);
        }
      }, 3000);
      
    } catch (error) {
      console.error('Error voting:', error);
      alert('Error submitting vote. Please try again.');
    }
  };

  const goToResults = () => {
    setUnvotedQuestions([]);
    setCurrentQuestion(null);
  };

  if (window.location.pathname === '/secret-analytics-dashboard-kate-2026') {
    return <Analytics />;
  }

  if (window.location.pathname === '/terms') {
    return <Terms />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-atc-purple flex items-center justify-center">
        <div className="text-atc-yellow text-3xl font-bold animate-pulse">
          Loading...
        </div>
      </div>
    );
  }

  if (allQuestions.length === 0) {
    return (
      <div className="min-h-screen bg-atc-purple flex flex-col">
        <div className="flex-1">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center mt-20">
              <h1 className="text-5xl font-bold text-atc-yellow mb-6">Ask the Class</h1>
              <p className="text-white text-2xl mb-8">No questions yet! Be the first to ask.</p>
              <button
                onClick={() => setShowSubmitModal(true)}
                className="bg-atc-yellow hover:bg-yellow-400 text-atc-purple font-bold py-4 px-8 rounded-lg text-xl transition-colors"
              >
                + Ask a Question
              </button>
            </div>
          </div>
        </div>
        <footer className="py-4 text-center">
          <a 
            href="/terms" 
            className="text-atc-yellow hover:text-yellow-300 text-sm underline"
          >
            Terms & Conditions
          </a>
        </footer>
        {showSubmitModal && (
          <SubmitQuestion 
            onClose={() => setShowSubmitModal(false)}
            onSubmit={loadQuestionsAndVotes}
          />
        )}
      </div>
    );
  }

  if (unvotedQuestions.length === 0) {
    return (
      <div className="min-h-screen bg-atc-purple flex flex-col">
        <div className="flex-1">
          <div className="bg-atc-purple border-b-4 border-atc-yellow">
            <div className="container mx-auto px-4 py-6 flex justify-between items-center">
              <div className="flex items-center gap-4">
                <img 
                  src="/logo.png" 
                  alt="Ask the Class" 
                  className="h-16 w-16"
                />
                <h1 className="text-4xl font-bold text-atc-yellow">Ask the Class</h1>
              </div>
              <button
                onClick={() => window.location.reload()}
                className="bg-atc-yellow hover:bg-yellow-400 text-atc-purple font-bold py-3 px-6 rounded-lg text-lg transition-colors"
              >
                ← Back to Voting
              </button>
            </div>
          </div>

          <div className="container mx-auto px-4 py-8 max-w-4xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-atc-yellow mb-4">
                🎉 You've voted on everything!
              </h2>
              <p className="text-white text-xl mb-6">
                Here's what the class decided:
              </p>
              <button
                onClick={() => setShowSubmitModal(true)}
                className="bg-atc-yellow hover:bg-yellow-400 text-atc-purple font-bold py-4 px-8 rounded-lg text-xl transition-colors"
              >
                + Ask Another Question
              </button>
            </div>

            <div className="space-y-6">
              {votedQuestions
                .sort((a, b) => (b.totalVotes || 0) - (a.totalVotes || 0))
                .map(question => {
  const totalVotes = question.totalVotes || 0;
  const percentA = totalVotes > 0 ? Math.round((question.votesA / totalVotes) * 100) : 0;
  const percentB = totalVotes > 0 ? Math.round((question.votesB / totalVotes) * 100) : 0;
                  
                  const getTimeRemaining = () => {
                    if (!question.createdAt) return 'Expired';
                    
                    const createdDate = question.createdAt.toDate();
                    const expiresAt = new Date(createdDate.getTime() + (24 * 60 * 60 * 1000));
                    const now = new Date();
                    const timeLeft = expiresAt - now;
                    
                    if (timeLeft <= 0) return 'Expired';
                    
                    const hoursLeft = Math.floor(timeLeft / (1000 * 60 * 60));
                    const minutesLeft = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
                    
                    if (hoursLeft > 0) {
                      return `Expires in ${hoursLeft}h ${minutesLeft}m`;
                    } else if (minutesLeft > 0) {
                      return `Expires in ${minutesLeft}m`;
                    } else {
                      return 'Expires soon';
                    }
                  };
                  
                  return (
                    <div key={question.id} className="bg-white rounded-lg shadow-lg p-6 border-4 border-atc-yellow">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-xl font-bold text-atc-purple flex-1">{question.text}</h3>
                        <span className="text-xs font-semibold text-orange-600 bg-orange-100 px-3 py-1 rounded-full ml-2 whitespace-nowrap">
                          ⏱️ {getTimeRemaining()}
                        </span>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="bg-green-100 rounded-lg p-4">
                          <div className="flex justify-between items-center">
                            <span className="text-lg font-semibold">👍 YES</span>
                            <span className="text-2xl font-bold text-atc-purple">
                              {question.votesA || 0} ({percentA}%)
                            </span>
                          </div>
                        </div>
                        
                        <div className="bg-red-100 rounded-lg p-4">
                          <div className="flex justify-between items-center">
                            <span className="text-lg font-semibold">👎 NO</span>
                            <span className="text-2xl font-bold text-atc-purple">
  {question.votesB || 0} ({percentB}%)
</span>
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-500 mt-4 text-center">
                        Total votes: {totalVotes}
                      </p>
                    </div>
                  );
                })}
            </div>

            <div className="fixed bottom-8 right-8">
              <button
                onClick={() => setShowSubmitModal(true)}
                className="bg-atc-yellow hover:bg-yellow-400 text-atc-purple font-bold py-4 px-6 rounded-full text-lg shadow-2xl transition-all hover:scale-110"
              >
                + Ask Question
              </button>
            </div>
          </div>
        </div>

        <footer className="py-4 text-center">
          <a 
            href="/terms" 
            className="text-atc-yellow hover:text-yellow-300 text-sm underline"
          >
            Terms & Conditions
          </a>
        </footer>

        {showSubmitModal && (
          <SubmitQuestion 
            onClose={() => setShowSubmitModal(false)}
            onSubmit={loadQuestionsAndVotes}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-atc-purple flex flex-col">
      <div className="flex-1">
        <div className="bg-atc-purple border-b-4 border-atc-yellow">
          <div className="container mx-auto px-4 py-6 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <img 
                src="/logo.png" 
                alt="Ask the Class" 
                className="h-16 w-16"
              />
              <h1 className="text-4xl font-bold text-atc-yellow">Ask the Class</h1>
            </div>
            <div className="flex gap-3">
              {votedQuestions.length > 0 && (
                <button
                  onClick={goToResults}
                  className="bg-white hover:bg-gray-100 text-atc-purple font-bold py-3 px-6 rounded-lg text-lg transition-colors border-2 border-atc-yellow"
                >
                  📊 View Results
                </button>
              )}
              <button
                onClick={() => setShowSubmitModal(true)}
                className="bg-atc-yellow hover:bg-yellow-400 text-atc-purple font-bold py-3 px-6 rounded-lg text-lg transition-colors"
              >
                + Ask Question
              </button>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8 max-w-2xl">
          {currentQuestion && (
            <QuestionCard 
              question={currentQuestion}
              onVote={handleVote}
              hasVoted={justVoted}
            />
          )}
          
          <div className="text-center text-white mt-6">
            <p className="text-lg">
              {unvotedQuestions.length} questions left to vote on
            </p>
            {hasVotedInSession && votedQuestions.length > 0 && (
              <p className="text-sm text-atc-yellow mt-2">
                {votedQuestions.length} voted ✓
              </p>
            )}
          </div>
        </div>
      </div>

      <footer className="py-4 text-center">
        <a 
          href="/terms" 
          className="text-atc-yellow hover:text-yellow-300 text-sm underline"
        >
          Terms & Conditions
        </a>
      </footer>

      {showSubmitModal && (
        <SubmitQuestion 
          onClose={() => setShowSubmitModal(false)}
          onSubmit={loadQuestionsAndVotes}
        />
      )}
    </div>
  );
}

export default App;